import { useState } from 'react'
import { fmtEur, fmtTime } from '@/shared/lib'
import { useDripWinners } from '@/shared/hooks'
import type { Winner } from '@/shared/types'

const countryFlag = (code: string) =>
  String.fromCodePoint(...[...code.toUpperCase()].map(c => 0x1f1e6 - 65 + c.charCodeAt(0)))

function formatTrader(row: Winner) {
  const flag = countryFlag(row.country_code)
  return `${flag} ${row.short_name}`
}

const PAGE_SIZE = 20

export function WinnersTable({ winners }: { winners: Winner[] }) {
  const { visibleWinners, freshId } = useDripWinners(winners)
  const [page, setPage] = useState(0)

  if (visibleWinners.length === 0) return null

  const totalPages = Math.ceil(visibleWinners.length / PAGE_SIZE)
  const pageWinners = visibleWinners.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  return (
    <div className="card overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 sm:px-6 py-3 sm:py-4 border-b border-white/[0.06]">
        <div className="flex items-center gap-2 sm:gap-3">
          <p className="text-sm sm:text-lg font-extrabold text-white">Live Profit Feed</p>
          <span className="inline-flex items-center gap-1 sm:gap-1.5 text-[10px] sm:text-xs font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full">
            <span className="live-dot" />
            Real-time
          </span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">{visibleWinners.length} trades</p>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs font-bold text-gray-400 hover:text-white bg-white/[0.04] rounded disabled:opacity-30 disabled:cursor-not-allowed"
              >←</button>
              <span className="text-[10px] sm:text-xs text-gray-500 px-1 sm:px-2">{page + 1}/{totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page === totalPages - 1}
                className="px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs font-bold text-gray-400 hover:text-white bg-white/[0.04] rounded disabled:opacity-30 disabled:cursor-not-allowed"
              >→</button>
            </div>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/[0.04] text-left bg-white/[0.02]">
              <th className="px-2.5 sm:px-6 py-2 sm:py-3 text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Trade #</th>
              <th className="px-2.5 sm:px-6 py-2 sm:py-3 text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Time UTC</th>
              <th className="px-2.5 sm:px-6 py-2 sm:py-3 text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Trader</th>
              <th className="px-2.5 sm:px-6 py-2 sm:py-3 text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Profit</th>
              <th className="px-2.5 sm:px-6 py-2 sm:py-3 text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Account</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/[0.03]">
            {pageWinners.map(row => (
              <tr key={row.id}
                  className={`transition-all duration-500 ${freshId === row.id ? 'animate-slide-down bg-emerald-500/[0.06]' : 'hover:bg-white/[0.02]'}`}>
                <td className="px-2.5 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm text-gray-500 font-mono">#{row.deal_number}</td>
                <td className="px-2.5 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm text-gray-500 font-mono hidden sm:table-cell">{fmtTime(row.created_at)}</td>
                <td className="px-2.5 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-bold text-white truncate max-w-[100px] sm:max-w-none">{formatTrader(row)}</td>
                <td className="px-2.5 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-bold text-emerald-400 text-right whitespace-nowrap">+{fmtEur(row.amount)}</td>
                <td className="px-2.5 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm text-gray-600 font-mono hidden sm:table-cell">{row.iban}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
